import React, { Suspense, lazy, useState, useEffect, useCallback, memo, useRef } from "react";
import PropTypes from "prop-types";
import CustomTree from "./CustomTree2";
import useIsPhoneSize from "layouts/useIsPhoneSize";
import { toggleExpandedForAll } from "@nosferatu500/react-sortable-tree";
import SearchBar from "components/SearchBar";
import { Button } from "reactstrap";
import { FaPen, FaPlus } from "react-icons/fa";
import { BsTrash } from "react-icons/bs";
import PDFViewerWithDownload from "components/PDFViewerWithDownload";
import { MdCancel } from "react-icons/md";
import StructureExcel from "./StructureExcel";

const CollectionsSummaryPDF = lazy(() => import("./StructurePdf"));

const ExportDropdown = memo(({ treeData, title, onPdfClick }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block" }}>
      <button
        className="btn btn-primary sm-btn shadow-none sm-btn-1"
        onClick={() => setOpen(!open)}
      >
        EXPORTS TO ▼
      </button>
      {open && (
        <div style={{ position: "absolute", background: "#fff", border: "1px solid #ccc", zIndex: 1000, minWidth: "120px" }}>
          <div style={{ padding: "8px", cursor: "pointer" }} onClick={() => setOpen(false)}>
            <StructureExcel data={treeData} title={title} />
          </div>
          <div style={{ padding: "8px", cursor: "pointer" }} onClick={() => { onPdfClick(); setOpen(false); }}>
            PDF
          </div>
        </div>
      )}
    </div>
  );
});

function StructureTree({
  editNode = (f) => f,
  deleteNode = (f) => f,
  addChild = (f) => f,
  treeData = [],
  title = "",
}) {
  const [searchText, setSearchText] = useState("");
  const [filteredTreeData, setFilteredTreeData] = useState([]);

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
  };

useEffect(() => {
  if (!searchText) {
    const setExpanded = (nodes, isRoot) =>
      nodes.map((node) => ({
        ...node,
        expanded: isRoot ? true : false,
        children: node.children ? setExpanded(node.children, false) : [],
      }));
    setFilteredTreeData(setExpanded(treeData, true));
  } else {
    const filterTree = (data) =>
      data
        .map((node) => {
          if (
            node.head?.toLowerCase().includes(searchText?.toLowerCase()) ||
            node.description?.toLowerCase().includes(searchText?.toLowerCase())
          ) {
            return {
              ...node,
              expanded: true, // Expand nodes that match search
              children: node?.children ? filterTree(node?.children) : [],
            };
          }
          const filteredChildren = node?.children ? filterTree(node?.children) : [];
          if (filteredChildren.length > 0) {
            return {
              ...node,
              expanded: true, // Expand parent if children match
              children: filteredChildren,
            };
          }
          return null;
        })
        .filter((node) => node !== null);
    setFilteredTreeData(filterTree(treeData));
  }
}, [searchText, treeData]);

  function expand(shouldExpand) {
    setFilteredTreeData(
      toggleExpandedForAll({
        treeData: filteredTreeData,
        expanded: shouldExpand,
      })
    );
  }

  function expandAll() {
    expand(true);
  }

  function collapseAll() {
    expand(false);
  }

  const [print, setPrint] = useState(false);

  const open = useCallback(() => {
    setPrint(true);
  }, []);

  const close = () => {
    setPrint(false);
  };

  const isPhoneSize = useIsPhoneSize();

  return (
    <>
      {print ? (
        <div>
          <Button color="danger" onClick={close}>
            <MdCancel /> Close
          </Button>

          <Suspense fallback={<div>Loading PDF...</div>}>
            <PDFViewerWithDownload
              width="1200"
              height="600"
              fileName="structure.pdf"
              document={<CollectionsSummaryPDF data={treeData} />}
            />
          </Suspense>
        </div>
      ) : (
        <div>
          <div className="row">
            <div className="col-md-4 ml-5 m-sm-5 mb-1 mt-0 pl-5">
              <SearchBar
                placeholder="Search By code or description"
                filterText={searchText}
                onFilterTextChange={handleSearchChange}
              />
            </div>
            <div
              className={`col-md-6  ${isPhoneSize} ? d-flex justify-content-center align-items-center  : "" `}
            >
              <button
                onClick={expandAll}
                className="btn btn-primary mb-2 sm-btn"
                style={{
                  marginRight: "10px",
                  marginLeft: "10px",
                  padding: isPhoneSize ? "6px" : "",
                }}
              >
                EXPAND ALL 
              </button>
              <button
                onClick={collapseAll}
                className="btn btn-primary mb-2 sm-btn"
                style={{
                  marginRight: "10px",
                  marginLeft: "10px",
                  padding: isPhoneSize ? "6px" : "",
                }}
              >
                COLLAPSE ALL
              </button>
              <ExportDropdown treeData={treeData} title={title} onPdfClick={open} />
            </div>
          </div>
          <div id="print_tree">
            {/* {JSON.stringify(title)} */}
            <CustomTree
              onChange={(newTreeData) => setFilteredTreeData(newTreeData)}
              generateNodeProps={({ node, path }) => ({
                title: (
                  <span
                    style={{
                      textTransform: node.children.length
                        ? "uppercase"
                        : "capitalize",
                    }}
                  >
                    {`${node.head}-${node.description}`}
                  </span>
                ),
                buttons: [
                  <button
                    className="btn btn-primary btn-sm mr-5 ml-5 no-print"
                    onClick={() => addChild(node)}
                    style={{ marginRight: "5px", color: "white" }}
                    key="addChild"
                  >
                    <FaPlus />
                  </button>,
                  <button
                    className="btn btn-primary btn-sm mr-5 ml-5 no-print"
                    onClick={() => editNode(node)}
                    style={{ marginRight: "5px" }}
                    key="editNode"
                  >
                    <FaPen />
                  </button>,
                  !node.children.length && (
                    <button
                      className="btn btn-danger btn-sm ml-3 no-print"
                      onClick={() => {
                        deleteNode(node);
                      }}
                      key="deleteNode"
                    >
                      <BsTrash />
                    </button>
                  ),
                ],
              })}
              treeData={filteredTreeData}
            />
          </div>
        </div>
      )}
    </>
  );
}

StructureTree.propTypes = {
  editNode: PropTypes.func,
  deleteNode: PropTypes.func,
  addChild: PropTypes.func,
  treeData: PropTypes.array,
  title: PropTypes.string,
};

export default StructureTree;
export { toggleExpandedForAll };
